import React, { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { BarChart3, Check, Pencil, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
    useAddBloggerMutation,
    useGetBloggersQuery,
    useGetBloggerStatsQuery,
    useRemoveBloggerMutation,
    useUpdateBloggerMutation,
} from "../api";
import BloggerInvitationTree from "../components/bloggers/BloggerInvitationTree";
import BloggerStatsModal from "../components/bloggers/BloggerStatsModal";
import { VipSection } from "../components/VipSection";
import {
    formatTelegramUsername,
    getBloggerLabel,
} from "../components/bloggers/bloggerLabels";
import { UserAutocomplete } from "../components/UserAutocomplete";
import Notification from "../components/ui/Notification";
import { useNotification } from "../hooks/useNotification";

type DatePreset = "all" | "month" | "last30" | "custom";
type DetailTab = "invitation" | "placement";

const formatUtcDate = (date: Date): string => date.toISOString().slice(0, 10);

const getPresetRange = (
    preset: DatePreset,
    customFrom: string,
    customTo: string
): { date_from?: string; date_to?: string; ready: boolean } => {
    if (preset === "all") {
        return { ready: true };
    }

    if (preset === "custom") {
        return {
            date_from: customFrom || undefined,
            date_to: customTo || undefined,
            ready: Boolean(customFrom && customTo && customTo >= customFrom),
        };
    }

    const now = new Date();
    const today = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
    );

    if (preset === "month") {
        return {
            date_from: formatUtcDate(
                new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1))
            ),
            date_to: formatUtcDate(today),
            ready: true,
        };
    }

    const from = new Date(today);
    from.setUTCDate(from.getUTCDate() - 29);

    return {
        date_from: formatUtcDate(from),
        date_to: formatUtcDate(today),
        ready: true,
    };
};

const getApiMessage = (error: unknown, fallback: string): string => {
    if (
        typeof error === "object" &&
        error !== null &&
        "data" in error &&
        typeof error.data === "object" &&
        error.data !== null
    ) {
        if (
            "message" in error.data &&
            typeof error.data.message === "string"
        ) {
            return error.data.message;
        }

        if (
            "errors" in error.data &&
            typeof error.data.errors === "object" &&
            error.data.errors !== null &&
            "display_name" in error.data.errors &&
            Array.isArray(error.data.errors.display_name)
        ) {
            return String(error.data.errors.display_name[0]);
        }
    }

    return fallback;
};

const formatCount = (value: number): string => value.toLocaleString();

const formatPercent = (value: number, total: number): string =>
    total > 0 ? `${Math.round((value / total) * 100)}%` : "0%";

const BloggersPage: React.FC = () => {
    const { t } = useTranslation("common");
    const { notification, showNotification, hideNotification } =
        useNotification();
    const editInputRef = useRef<HTMLInputElement>(null);
    const [userIdentifier, setUserIdentifier] = useState("");
    const [selectedBloggerId, setSelectedBloggerId] = useState<number | null>(
        null
    );
    const [preset, setPreset] = useState<DatePreset>("all");
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");
    const [detailTab, setDetailTab] = useState<DetailTab>("invitation");
    const [isChartOpen, setIsChartOpen] = useState(false);
    const [isEditingName, setIsEditingName] = useState(false);
    const [displayName, setDisplayName] = useState("");
    const [editError, setEditError] = useState<string | null>(null);
    const [treeRefreshKey, setTreeRefreshKey] = useState(0);

    const {
        data: bloggersResponse,
        isLoading: isLoadingBloggers,
        isError: isBloggersError,
        refetch: refetchBloggers,
    } = useGetBloggersQuery();
    const bloggers = useMemo(
        () => bloggersResponse?.data ?? [],
        [bloggersResponse?.data]
    );
    const totals = bloggersResponse?.totals;
    const selectedBlogger = useMemo(
        () =>
            bloggers.find((blogger) => blogger.id === selectedBloggerId) ??
            null,
        [bloggers, selectedBloggerId]
    );

    const [addBlogger, { isLoading: isAdding }] = useAddBloggerMutation();
    const [removeBlogger, { isLoading: isRemoving }] =
        useRemoveBloggerMutation();
    const [updateBlogger, { isLoading: isUpdating }] =
        useUpdateBloggerMutation();

    useEffect(() => {
        if (selectedBloggerId === null && bloggers.length > 0) {
            setSelectedBloggerId(bloggers[0].id);
        }
    }, [bloggers, selectedBloggerId]);

    useEffect(() => {
        setIsEditingName(false);
        setEditError(null);
        setDetailTab("invitation");
    }, [selectedBloggerId]);

    useEffect(() => {
        if (isEditingName) {
            editInputRef.current?.focus();
            editInputRef.current?.select();
        }
    }, [isEditingName]);

    const range = useMemo(
        () => getPresetRange(preset, customFrom, customTo),
        [preset, customFrom, customTo]
    );

    const statsArgs = selectedBloggerId
        ? {
              bloggerId: selectedBloggerId,
              date_from: range.date_from,
              date_to: range.date_to,
          }
        : { bloggerId: 0 };

    const {
        data: statsResponse,
        isLoading: isLoadingStats,
        isFetching: isFetchingStats,
        isError: isStatsError,
        refetch: refetchStats,
    } = useGetBloggerStatsQuery(statsArgs, {
        skip: selectedBloggerId === null || !range.ready,
    });

    const handleAdd = async (event: FormEvent): Promise<void> => {
        event.preventDefault();
        const identifier = userIdentifier.trim();
        if (!identifier) {
            showNotification(t("bloggers.identifier_required"), "warning");
            return;
        }

        try {
            const response = await addBlogger(identifier).unwrap();
            setSelectedBloggerId(response.data.id);
            setUserIdentifier("");
            showNotification(t("bloggers.added"), "success");
        } catch (error) {
            showNotification(
                getApiMessage(error, t("bloggers.add_error")),
                "error"
            );
        }
    };

    const handleRemove = async (bloggerId: number): Promise<void> => {
        if (!window.confirm(t("bloggers.remove_confirm"))) {
            return;
        }

        const nextBlogger = bloggers.find(
            (blogger) => blogger.id !== bloggerId
        );

        try {
            await removeBlogger(bloggerId).unwrap();
            if (selectedBloggerId === bloggerId) {
                setSelectedBloggerId(nextBlogger?.id ?? null);
            }
            showNotification(t("bloggers.removed"), "success");
        } catch (error) {
            showNotification(
                getApiMessage(error, t("bloggers.remove_error")),
                "error"
            );
        }
    };

    const beginEditingName = (): void => {
        if (!selectedBlogger) {
            return;
        }

        setDisplayName(selectedBlogger.display_name ?? "");
        setEditError(null);
        setIsEditingName(true);
    };

    const cancelEditingName = (): void => {
        setIsEditingName(false);
        setEditError(null);
    };

    const saveDisplayName = async (): Promise<void> => {
        if (!selectedBlogger || isUpdating) {
            return;
        }

        if (displayName.trim().length > 255) {
            setEditError(t("bloggers.display_name_too_long"));
            return;
        }

        try {
            await updateBlogger({
                bloggerId: selectedBlogger.id,
                display_name: displayName,
            }).unwrap();
            setIsEditingName(false);
            setEditError(null);
            showNotification(t("bloggers.name_saved"), "success");
        } catch (error) {
            setEditError(getApiMessage(error, t("bloggers.name_save_error")));
        }
    };

    const stats = statsResponse?.data;
    const structureByLevel = stats?.structure_by_level ?? [];
    const metricRows = stats
        ? [
              {
                  label: t("bloggers.registered"),
                  direct: stats.direct.registered,
                  invitation: stats.invitation_tree.registered,
                  placement: stats.entire_structure.registered,
              },
              {
                  label: t("bloggers.ever_paid"),
                  direct: stats.direct.ever_paid,
                  invitation: stats.invitation_tree.ever_paid,
                  placement: stats.entire_structure.ever_paid,
              },
              {
                  label: t("bloggers.current_active"),
                  direct: stats.direct.current_active,
                  invitation: stats.invitation_tree.current_active,
                  placement: stats.entire_structure.current_active,
              },
          ]
        : [];
    const summaryCards = [
        {
            label: t("bloggers.total_direct_invited"),
            value: totals?.registered,
        },
        {
            label: t("bloggers.total_direct_paid"),
            value: totals?.ever_paid,
        },
        {
            label: t("bloggers.total_direct_active"),
            value: totals?.current_active,
        },
    ];
    const selectedBloggerLabel = selectedBlogger
        ? getBloggerLabel(selectedBlogger)
        : null;
    const selectedBloggerTelegram = selectedBlogger
        ? formatTelegramUsername(selectedBlogger.user.telegram_username)
        : null;

    const handleRefresh = (): void => {
        refetchBloggers();
        setTreeRefreshKey((value) => value + 1);

        if (selectedBloggerId !== null && range.ready) {
            refetchStats();
        }
    };

    return (
        <div className="space-y-6 p-4 md:p-6">
            <Notification
                message={notification.message}
                type={notification.type}
                isVisible={notification.isVisible}
                onClose={hideNotification}
            />

            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {t("bloggers.title")}
                    </h1>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {t("bloggers.description")}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={handleRefresh}
                    disabled={
                        isLoadingBloggers ||
                        isLoadingStats ||
                        isFetchingStats
                    }
                    className="self-start rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {isLoadingBloggers || isFetchingStats
                        ? t("bloggers.refreshing")
                        : t("bloggers.refresh")}
                </button>
            </div>

            <section aria-label={t("bloggers.global_totals")}>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {t("bloggers.global_totals")}
                    </h2>
                    <button
                        type="button"
                        onClick={() => setIsChartOpen(true)}
                        className="inline-flex items-center gap-2 rounded-md border border-blue-200 bg-white px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50 dark:border-blue-800 dark:bg-gray-800 dark:text-blue-300 dark:hover:bg-blue-950/30"
                    >
                        <BarChart3 size={17} aria-hidden="true" />
                        {t("bloggers.open_chart")}
                    </button>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    {summaryCards.map((card) => (
                        <div
                            key={card.label}
                            className="rounded-lg bg-white p-4 shadow dark:bg-gray-800"
                        >
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                {card.label}
                            </p>
                            <p className="mt-3 text-2xl font-semibold text-gray-900 dark:text-white">
                                {isLoadingBloggers
                                    ? t("bloggers.loading_total")
                                    : isBloggersError
                                      ? "—"
                                      : formatCount(card.value ?? 0)}
                            </p>
                        </div>
                    ))}
                </div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    {t("bloggers.all_time_direct_caption")}
                </p>
            </section>

            <form
                onSubmit={handleAdd}
                className="rounded-lg bg-white p-4 shadow dark:bg-gray-800"
            >
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t("bloggers.add_label")}
                </label>
                <div className="flex flex-col gap-3 sm:flex-row">
                    <div className="flex-1">
                        <UserAutocomplete
                            value={userIdentifier}
                            onChange={setUserIdentifier}
                            placeholder={t("bloggers.search_placeholder")}
                            disabled={isAdding}
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={isAdding || !userIdentifier.trim()}
                        className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {isAdding
                            ? t("bloggers.adding")
                            : t("bloggers.add")}
                    </button>
                </div>
            </form>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
                <section className="rounded-lg bg-white p-4 shadow dark:bg-gray-800">
                    <h2 className="mb-3 font-semibold text-gray-900 dark:text-white">
                        {t("bloggers.saved")}
                    </h2>

                    {isLoadingBloggers ? (
                        <p className="text-sm text-gray-500">
                            {t("bloggers.loading")}
                        </p>
                    ) : isBloggersError ? (
                        <div className="space-y-3">
                            <p className="text-sm text-red-600">
                                {t("bloggers.list_error")}
                            </p>
                            <button
                                type="button"
                                onClick={() => refetchBloggers()}
                                className="text-sm text-blue-600 hover:underline"
                            >
                                {t("bloggers.retry")}
                            </button>
                        </div>
                    ) : bloggers.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            {t("bloggers.empty")}
                        </p>
                    ) : (
                        <div className="space-y-2">
                            {bloggers.map((blogger) => {
                                const isSelected =
                                    selectedBloggerId === blogger.id;
                                const bloggerLabel = getBloggerLabel(blogger);
                                const telegram = formatTelegramUsername(
                                    blogger.user.telegram_username
                                );

                                return (
                                    <div
                                        key={blogger.id}
                                        className={`flex items-center gap-2 rounded-md border p-3 ${
                                            isSelected
                                                ? "border-blue-500 bg-blue-50 dark:bg-blue-950/30"
                                                : "border-gray-200 dark:border-gray-700"
                                        }`}
                                    >
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setSelectedBloggerId(blogger.id)
                                            }
                                            className="min-w-0 flex-1 text-left"
                                        >
                                            <span className="block truncate font-medium text-gray-900 dark:text-white">
                                                {bloggerLabel}
                                            </span>
                                            {telegram &&
                                                telegram !== bloggerLabel && (
                                                <span className="block truncate text-xs text-sky-600 dark:text-sky-400">
                                                    {telegram}
                                                </span>
                                            )}
                                            <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                                                #{blogger.user.id}
                                                {blogger.user.secret_cookie
                                                    ? ` · ${blogger.user.secret_cookie}`
                                                    : ""}
                                            </span>
                                        </button>
                                        <span
                                            aria-label={t(
                                                "bloggers.direct_invited_count",
                                                {
                                                    count:
                                                        blogger.direct
                                                            .registered,
                                                }
                                            )}
                                            className="shrink-0 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                                        >
                                            {formatCount(
                                                blogger.direct.registered
                                            )}
                                        </span>
                                        <button
                                            type="button"
                                            disabled={isRemoving}
                                            onClick={() =>
                                                handleRemove(blogger.id)
                                            }
                                            aria-label={t(
                                                "bloggers.remove_label"
                                            )}
                                            className="px-2 py-1 text-red-600 hover:text-red-700 disabled:opacity-50"
                                        >
                                            ×
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </section>

                <section className="min-w-0 rounded-lg bg-white p-4 shadow dark:bg-gray-800 md:p-6">
                    {selectedBloggerId === null || !selectedBlogger ? (
                        <div className="py-12 text-center text-gray-500 dark:text-gray-400">
                            {t("bloggers.select_prompt")}
                        </div>
                    ) : (
                        <div className="space-y-5">
                            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                                <div className="min-w-0">
                                    {isEditingName ? (
                                        <div>
                                            <div className="flex flex-wrap items-center gap-2">
                                                <input
                                                    ref={editInputRef}
                                                    value={displayName}
                                                    maxLength={255}
                                                    aria-label={t(
                                                        "bloggers.display_name"
                                                    )}
                                                    onChange={(event) => {
                                                        setDisplayName(
                                                            event.target.value
                                                        );
                                                        setEditError(null);
                                                    }}
                                                    onKeyDown={(event) => {
                                                        if (
                                                            event.key ===
                                                            "Enter"
                                                        ) {
                                                            event.preventDefault();
                                                            saveDisplayName();
                                                        } else if (
                                                            event.key ===
                                                            "Escape"
                                                        ) {
                                                            cancelEditingName();
                                                        }
                                                    }}
                                                    className="min-w-0 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-lg font-semibold text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                                                    placeholder={t(
                                                        "bloggers.display_name_placeholder"
                                                    )}
                                                />
                                                <button
                                                    type="button"
                                                    disabled={isUpdating}
                                                    onClick={saveDisplayName}
                                                    aria-label={t(
                                                        "bloggers.save_name"
                                                    )}
                                                    className="rounded-md p-2 text-emerald-600 hover:bg-emerald-50 disabled:opacity-50 dark:hover:bg-emerald-950/30"
                                                >
                                                    <Check
                                                        size={18}
                                                        aria-hidden="true"
                                                    />
                                                </button>
                                                <button
                                                    type="button"
                                                    disabled={isUpdating}
                                                    onClick={cancelEditingName}
                                                    aria-label={t(
                                                        "bloggers.cancel_name"
                                                    )}
                                                    className="rounded-md p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-50 dark:hover:bg-gray-700"
                                                >
                                                    <X
                                                        size={18}
                                                        aria-hidden="true"
                                                    />
                                                </button>
                                            </div>
                                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                {t(
                                                    "bloggers.clear_name_caption"
                                                )}
                                            </p>
                                            {editError && (
                                                <p
                                                    role="alert"
                                                    className="mt-1 text-sm text-red-600"
                                                >
                                                    {editError}
                                                </p>
                                            )}
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={beginEditingName}
                                            aria-label={t(
                                                "bloggers.edit_name",
                                                {
                                                    name: getBloggerLabel(
                                                        selectedBlogger
                                                    ),
                                                }
                                            )}
                                            className="group inline-flex max-w-full items-center gap-2 text-left"
                                        >
                                            <span className="truncate text-lg font-semibold text-gray-900 dark:text-white">
                                                {selectedBloggerLabel}
                                            </span>
                                            <Pencil
                                                size={15}
                                                aria-hidden="true"
                                                className="shrink-0 text-gray-400 group-hover:text-blue-600"
                                            />
                                        </button>
                                    )}
                                    {selectedBloggerTelegram &&
                                        selectedBloggerTelegram !==
                                            selectedBloggerLabel && (
                                        <p className="text-sm text-sky-600 dark:text-sky-400">
                                            {selectedBloggerTelegram}
                                        </p>
                                    )}
                                    {isFetchingStats && (
                                        <span className="text-xs text-gray-500">
                                            {t("bloggers.refreshing")}
                                        </span>
                                    )}
                                </div>

                                <div className="flex flex-wrap gap-2">
                                    {(
                                        [
                                            "all",
                                            "month",
                                            "last30",
                                            "custom",
                                        ] as DatePreset[]
                                    ).map((value) => (
                                        <button
                                            type="button"
                                            key={value}
                                            onClick={() => setPreset(value)}
                                            className={`rounded-md px-3 py-1.5 text-sm ${
                                                preset === value
                                                    ? "bg-blue-600 text-white"
                                                    : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                                            }`}
                                        >
                                            {t(`bloggers.preset_${value}`)}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {preset === "custom" && (
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    <label className="text-sm text-gray-700 dark:text-gray-300">
                                        {t("bloggers.date_from")}
                                        <input
                                            type="date"
                                            value={customFrom}
                                            max={customTo || undefined}
                                            onChange={(event) =>
                                                setCustomFrom(event.target.value)
                                            }
                                            className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-700"
                                        />
                                    </label>
                                    <label className="text-sm text-gray-700 dark:text-gray-300">
                                        {t("bloggers.date_to")}
                                        <input
                                            type="date"
                                            value={customTo}
                                            min={customFrom || undefined}
                                            onChange={(event) =>
                                                setCustomTo(event.target.value)
                                            }
                                            className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-700"
                                        />
                                    </label>
                                    {!range.ready && (
                                        <p className="text-sm text-amber-600 sm:col-span-2">
                                            {t("bloggers.custom_range_required")}
                                        </p>
                                    )}
                                </div>
                            )}

                            <VipSection
                                userId={selectedBlogger.user.id}
                            />

                            {isStatsError ? (
                                <div className="space-y-3 py-10 text-center">
                                    <p className="text-red-600">
                                        {t("bloggers.stats_error")}
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => refetchStats()}
                                        className="text-sm text-blue-600 hover:underline"
                                    >
                                        {t("bloggers.retry")}
                                    </button>
                                </div>
                            ) : isLoadingStats ? (
                                <div className="py-12 text-center text-gray-500">
                                    {t("bloggers.loading_stats")}
                                </div>
                            ) : stats ? (
                                <>
                                    {stats.registration_period.date_from &&
                                        stats.registration_period.date_to && (
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                {t("bloggers.filtered_caption", {
                                                    from: stats
                                                        .registration_period
                                                        .date_from,
                                                    to: stats
                                                        .registration_period
                                                        .date_to,
                                                })}
                                            </p>
                                        )}

                                    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                            <thead className="bg-gray-50 dark:bg-gray-700">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-300">
                                                        {t("bloggers.metric")}
                                                    </th>
                                                    <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500 dark:text-gray-300">
                                                        {t(
                                                            "bloggers.direct_invited"
                                                        )}
                                                    </th>
                                                    <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500 dark:text-gray-300">
                                                        {t(
                                                            "bloggers.invitation_tree"
                                                        )}
                                                    </th>
                                                    <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500 dark:text-gray-300">
                                                        {t(
                                                            "bloggers.placement_structure"
                                                        )}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                                {metricRows.map((row) => (
                                                    <tr key={row.label}>
                                                        <td className="px-4 py-4 text-sm font-medium text-gray-900 dark:text-white">
                                                            {row.label}
                                                        </td>
                                                        <td className="px-4 py-4 text-right text-xl font-semibold text-gray-900 dark:text-white">
                                                            {formatCount(
                                                                row.direct
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-4 text-right text-xl font-semibold text-gray-900 dark:text-white">
                                                            {formatCount(
                                                                row.invitation
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-4 text-right text-xl font-semibold text-gray-900 dark:text-white">
                                                            {formatCount(
                                                                row.placement
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="space-y-4">
                                        <div
                                            role="tablist"
                                            aria-label={t(
                                                "bloggers.details_tabs"
                                            )}
                                            className="flex gap-1 border-b border-gray-200 dark:border-gray-700"
                                        >
                                            {(
                                                [
                                                    "invitation",
                                                    "placement",
                                                ] as DetailTab[]
                                            ).map((tab) => (
                                                <button
                                                    key={tab}
                                                    type="button"
                                                    role="tab"
                                                    aria-selected={
                                                        detailTab === tab
                                                    }
                                                    onClick={() =>
                                                        setDetailTab(tab)
                                                    }
                                                    className={`border-b-2 px-4 py-2 text-sm font-medium ${
                                                        detailTab === tab
                                                            ? "border-blue-600 text-blue-700 dark:text-blue-300"
                                                            : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                                    }`}
                                                >
                                                    {t(
                                                        `bloggers.${tab}_tab`
                                                    )}
                                                </button>
                                            ))}
                                        </div>

                                        {detailTab === "invitation" ? (
                                            <div
                                                role="tabpanel"
                                                className="space-y-3"
                                            >
                                                <div>
                                                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                                                        {t(
                                                            "bloggers.invitation_tree_title"
                                                        )}
                                                    </h3>
                                                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                        {t(
                                                            "bloggers.invitation_tree_caption"
                                                        )}
                                                    </p>
                                                </div>
                                                <BloggerInvitationTree
                                                    key={`${selectedBlogger.id}-${treeRefreshKey}`}
                                                    bloggerId={
                                                        selectedBlogger.id
                                                    }
                                                    rootUserId={
                                                        selectedBlogger.user.id
                                                    }
                                                />
                                            </div>
                                        ) : (
                                            <div
                                                role="tabpanel"
                                                className="space-y-3"
                                            >
                                                <div>
                                                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                                                        {t(
                                                            "bloggers.placement_levels"
                                                        )}
                                                    </h3>
                                                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                        {t(
                                                            "bloggers.placement_levels_caption"
                                                        )}
                                                    </p>
                                                </div>

                                                {structureByLevel.length ===
                                                0 ? (
                                                    <div className="rounded-lg border border-gray-200 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                                                        {t(
                                                            "bloggers.no_structure_levels"
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="max-h-96 overflow-auto rounded-lg border border-gray-200 dark:border-gray-700">
                                                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                                            <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700">
                                                                <tr>
                                                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-300">
                                                                        {t(
                                                                            "bloggers.level"
                                                                        )}
                                                                    </th>
                                                                    <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500 dark:text-gray-300">
                                                                        {t(
                                                                            "bloggers.people"
                                                                        )}
                                                                    </th>
                                                                    <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500 dark:text-gray-300">
                                                                        {t(
                                                                            "bloggers.ever_paid"
                                                                        )}
                                                                    </th>
                                                                    <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500 dark:text-gray-300">
                                                                        {t(
                                                                            "bloggers.current_active"
                                                                        )}
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                                                {structureByLevel.map(
                                                                    (level) => (
                                                                        <tr
                                                                            key={
                                                                                level.level
                                                                            }
                                                                        >
                                                                            <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                                                                                {
                                                                                    level.level
                                                                                }
                                                                            </td>
                                                                            <td className="px-4 py-3 text-right text-base font-semibold text-gray-900 dark:text-white">
                                                                                {formatCount(
                                                                                    level.registered
                                                                                )}
                                                                            </td>
                                                                            <td className="px-4 py-3 text-right whitespace-nowrap">
                                                                                <span className="text-base font-semibold text-gray-900 dark:text-white">
                                                                                    {formatCount(
                                                                                        level.ever_paid
                                                                                    )}
                                                                                </span>
                                                                                <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                                                                                    (
                                                                                    {formatPercent(
                                                                                        level.ever_paid,
                                                                                        level.registered
                                                                                    )}
                                                                                    )
                                                                                </span>
                                                                            </td>
                                                                            <td className="px-4 py-3 text-right whitespace-nowrap">
                                                                                <span className="text-base font-semibold text-gray-900 dark:text-white">
                                                                                    {formatCount(
                                                                                        level.current_active
                                                                                    )}
                                                                                </span>
                                                                                <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                                                                                    (
                                                                                    {formatPercent(
                                                                                        level.current_active,
                                                                                        level.registered
                                                                                    )}
                                                                                    )
                                                                                </span>
                                                                            </td>
                                                                        </tr>
                                                                    )
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : null}
                        </div>
                    )}
                </section>
            </div>

            {isChartOpen && (
                <BloggerStatsModal
                    isOpen
                    onClose={() => setIsChartOpen(false)}
                    bloggers={bloggers}
                />
            )}
        </div>
    );
};

export default BloggersPage;
